import { ff, getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import type { VCEntryType, ComponentsLogType } from '../types';
import { initDevTools } from '../vc-dev-tools/replay';

import { Observers } from './observers';

type PixelsToMap = { l: number; t: number; r: number; b: number };

type AbortReason = 'scroll' | 'keypress' | 'resize' | 'error';
type AbortReasonEnum = { [key: string]: AbortReason };

type GetVCResultType = {
	start: number;
	stop: number;
	tti: number;
};

const abortReason: AbortReasonEnum = {
	scroll: 'scroll',
	keypress: 'keypress',
	resize: 'resize',
	error: 'error',
};

export class VCObserver {
	/* abort logic */
	abortReason: { reason: null | AbortReason; info: string; timestamp: number } = {
		reason: null,
		info: '',
		timestamp: -1,
	};

	outOfBoundaryInfo = '';

	scrollCallback: null | ((e: Event) => void) = null;

	keyDownCallback: null | ((e: Event) => void) = null;

	resizeCallback: null | ((e: Event) => void) = null;

	/** config * */
	VCParts = ['75', '80', '85', '90', '95', '98', '99'];

	viewport = {
		w: 0,
		h: 0,
	};

	/* heatmap */
	arraySize = 0;

	heatmap: number[][];

	ssrInclusiveHeatmap: number[][];

	componentsLog: ComponentsLogType = {};

	active = false;

	totalTime = 0;

	observers: Observers;

	private _startMeasureTimestamp = -1;

	ssr = {
		reactRendered: -1,
	};

	constructor() {
		this.observers = new Observers();
		this.heatmap = this.getCleanHeatmap();
		this.ssrInclusiveHeatmap = this.getCleanHeatmap();
		initDevTools();
	}

	start() {
		this.setViewportSize();
		this.resetState();

		this.active = true;
		this.attachAbortListeners();
		this.observers.subscribeResults(this.handleUpdate);
		this.observers.observe();
	}

	stop() {
		this.observers.disconnect();
		if (this.scrollCallback && this.keyDownCallback && this.resizeCallback) {
			window.removeEventListener('wheel', this.scrollCallback);
			window.removeEventListener('keydown', this.keyDownCallback);
			window.removeEventListener('resize', this.resizeCallback);
		}
	}

	getAbortReason = () => {
		if (this.abortReason.reason === null) {
			return null;
		}

		const info = this.abortReason.info !== '' ? ` ${this.abortReason.info}` : '';
		return `${this.abortReason.reason}${info}`;
	};

	getVCResult = ({ start: _start, stop, tti }: GetVCResultType) => {
		this.measureStart();

		if (!this.active) {
			return {};
		}

		this.stop();

		const abortReason = this.getAbortReason();
		if (abortReason !== null) {
			return {
				'vc:state': false,
				'vc:abort:reason': abortReason,
				'vc:abort:timestamp': this.abortReason.timestamp,
			};
		}

		const { VC, VCBox, VCEntries, totalPainted } = this.calculateVC(this.ssrInclusiveHeatmap, true);

		const ssrVC = this.calculateVC(this.heatmap);

		const outOfBoundary = this.outOfBoundaryInfo ? { 'vc:oob': this.outOfBoundaryInfo } : {};
		const oldDomUpdates = ff('uip.vc.old-dom', false) ? { 'vc:old:dom': ssrVC.VCBox } : {};

		this.measureStop();

		// exposing data to devtools
		try {
			if (ff('uip.vc.devtools', false)) {
				window.__vc = {
					entries: VCEntries.rel,
					log: this.componentsLog,
					metrics: {
						'75': VC['75'],
						'80': VC['80'],
						'85': VC['85'],
						'90': VC['90'],
						'95': VC['95'],
						'98': VC['98'],
						'99': VC['99'],
						tti,
						ttai: stop,
					},
					heatmap: this.heatmap,
				};
			}
		} catch (e) {
			/*  do nothing */
		}
		// console.log('exposed data')

		const returnValue = {
			'vc:state': true,
			vc: VC,
			'vc:dom': VCBox,
			'vc:updates': VCEntries.rel.slice(0, 50), // max 50
			'vc:size': { ...this.viewport },
			'vc:time': Math.round(this.totalTime + this.observers.getTotalTime()),
			'vc:total': totalPainted,
			...outOfBoundary,
			'vc:old': ssrVC.VC,
			...oldDomUpdates,
		};

		return returnValue;
	};

	private calculateVC(heatmap: number[][], addToPerformance = false) {
		const lastUpdate: { [key: string]: number } = {};
		let totalPainted = 0;

		heatmap.forEach((line) => {
			line.forEach((entry) => {
				const rounded = Math.floor(entry);
				totalPainted += entry > 0 ? 1 : 0;
				if (rounded > 0) {
					lastUpdate[rounded] = lastUpdate[rounded] ? lastUpdate[rounded] + 1 : 1;
				}
			});
		});

		const entries: number[][] = Object.entries(lastUpdate)
			.map((a) => [parseInt(a[0], 10), a[1]])
			.sort((a, b) => (a[0] > b[0] ? 1 : -1));

		const VC: { [key: string]: number | null } = this.makeVCReturnObj<number>();
		const VCBox: { [key: string]: string[] | null } = this.makeVCReturnObj<string[]>();

		entries.reduce((acc = 0, v) => {
			const VCRatio = v[1] / totalPainted + acc;
			const time = v[0];
			this.VCParts.forEach((key) => {
				const value = parseInt(key, 10);
				if ((VC[key] === null || VC[key] === undefined) && VCRatio >= value / 100) {
					VC[key] = time;
					VCBox[key] = this.componentsLog[time]?.map((v) => v.targetName);
				}
			});
			return VCRatio;
		}, 0);

		try {
			if (addToPerformance) {
				this.VCParts.forEach((key) => {
					const duration = VC[key];
					if (duration !== null && duration !== undefined) {
						performance.measure(`VC${key}`, { start: 0, duration });
					}
				});
			}
		} catch (e) {
			/* empty */
		}

		const VCEntries = entries.reduce(
			(acc: { abs: number[][]; rel: VCEntryType[] }, [timestamp, entryPainted], i) => {
				const currentlyPainted = entryPainted + (acc.abs[i - 1]?.[1] || 0);
				const currentlyPaintedRatio = Math.round((currentlyPainted / totalPainted) * 1000) / 10;
				const logEntry = this.componentsLog[timestamp]?.map((v) => v.targetName);

				acc.abs.push([timestamp, currentlyPainted]);
				acc.rel.push({
					time: timestamp,
					vc: currentlyPaintedRatio,
					elements: logEntry,
				});
				return acc;
			},
			{ abs: [], rel: [] },
		);

		return { VC, VCBox, VCEntries, totalPainted };
	}

	setSSRElement(element: HTMLElement) {
		this.observers.setReactRootElement(element);
	}

	setReactRootRenderStart(startTime = performance.now()) {
		this.observers.setReactRootRenderStart(startTime);
	}

	setReactRootRenderStop(stopTime = performance.now()) {
		this.observers.setReactRootRenderStop(stopTime);
	}

	private handleUpdate = (
		rawTime: number,
		intersectionRect: DOMRectReadOnly,
		targetName: string,
		element: HTMLElement,
		ignored: boolean,
	) => {
		this.measureStart();

		if (this.abortReason.reason === null) {
			const time = Math.round(rawTime);
			const mappedValues = this.mapPixelsToHeatmap(
				intersectionRect.left,
				intersectionRect.top,
				intersectionRect.width,
				intersectionRect.height,
			);
			this.applyChangesToHeatMap(mappedValues, time, this.heatmap);

			if (!ignored) {
				this.applyChangesToHeatMap(mappedValues, time, this.ssrInclusiveHeatmap);
			}

			// console.log('handleUpdate', ignored, time, element === this.ssr.element, (intersectionRect.width * intersectionRect.height) / (this.viewport.w*this.viewport.h), element);

			if (!this.componentsLog[time]) {
				this.componentsLog[time] = [];
			}
			this.componentsLog[time].push({ element, intersectionRect, targetName, ignored });
		}
		// devtools export
		this.measureStop();
	};

	private setAbortReason(abortReason: AbortReason, timestamp: number, info = '') {
		if (this.abortReason.reason === null) {
			this.abortReason.reason = abortReason;
			this.abortReason.info = info;
			this.abortReason.timestamp = timestamp;
			this.detachAbortListeners();
		}
	}

	private resetState() {
		this.abortReason = {
			reason: null,
			info: '',
			timestamp: -1,
		};
		this.detachAbortListeners();
		this.arraySize = getFeatureFlagValue<number>('uip.vc.size', 200);
		this.heatmap = this.getCleanHeatmap();
		this.ssrInclusiveHeatmap = this.getCleanHeatmap();

		this.totalTime = 0;
		this.componentsLog = {};
	}

	private getCleanHeatmap() {
		return Array(this.arraySize)
			.fill('')
			.map(() => Array(this.arraySize).fill(0));
	}

	private setViewportSize() {
		let documentWidth;
		try {
			documentWidth = document.documentElement.clientWidth || 0;
		} catch (e) {
			documentWidth = 0;
		}

		let documentHeight;
		try {
			documentHeight = document.documentElement.clientHeight || 0;
		} catch (e) {
			documentHeight = 0;
		}

		this.viewport.w = Math.max(documentWidth, window.innerWidth || 0);
		this.viewport.h = Math.max(documentHeight, window.innerHeight || 0);
	}

	private mapPixelsToHeatmap = (
		left: number,
		top: number,
		width: number,
		height: number,
	): PixelsToMap => {
		const { w, h } = this.viewport;

		const l = Math.floor((left / w) * this.arraySize);
		const t = Math.floor((top / h) * this.arraySize);
		const r = Math.ceil(((left + width) / w) * this.arraySize);
		const b = Math.ceil(((top + height) / h) * this.arraySize);

		// that info is temporary to get info why it goes over boundary
		if (this.outOfBoundaryInfo === '') {
			let outOfBoundaryInfo = '';

			if (r > this.arraySize) {
				outOfBoundaryInfo += ` r ${r} ! ${left} ${width}`;
			}

			if (b > this.arraySize) {
				outOfBoundaryInfo += ` r ${r} ! ${top} ${height}`;
			}

			this.outOfBoundaryInfo = outOfBoundaryInfo;
		}

		// correct values to min - 0, max - arraySize
		const result = {
			l: Math.max(0, l),
			t: Math.max(0, t),
			r: Math.min(this.arraySize, r),
			b: Math.min(this.arraySize, b),
		};

		return result;
	};

	private applyChangesToHeatMap(a: PixelsToMap, time: number, heatmap: number[][]) {
		const { l, t, r, b } = a;
		const localHeatmap = heatmap;
		for (let row = t; row < b; row++) {
			for (let col = l; col < r; col++) {
				if (localHeatmap[row] === undefined) {
					try {
						this.setAbortReason(abortReason.error, time, `index - ${row}`);
					} catch (e) {
						this.setAbortReason(abortReason.error, time, 'row error');
					}
					return;
				} else {
					localHeatmap[row][col] = time;
				}
			}
		}
	}

	private makeVCReturnObj<T>() {
		const vc: { [key: string]: null | T } = {};
		this.VCParts.forEach((v) => {
			vc[v] = null;
		});
		return vc;
	}

	private attachAbortListeners = () => {
		this.scrollCallback = (e: Event) => {
			this.setAbortReason(abortReason.scroll, e.timeStamp);
		};
		this.keyDownCallback = (e: Event) => {
			this.setAbortReason(abortReason.keypress, e.timeStamp);
		};
		this.resizeCallback = (e: Event) => {
			this.setAbortReason(abortReason.resize, e.timeStamp);
		};

		if (this.scrollCallback && this.keyDownCallback && this.resizeCallback) {
			window.addEventListener('wheel', this.scrollCallback, { passive: true, once: true });
			window.addEventListener('keydown', this.keyDownCallback, { once: true });
			window.addEventListener('resize', this.resizeCallback, { once: true });
		}
	};

	private detachAbortListeners() {
		this.scrollCallback && window.removeEventListener('wheel', this.scrollCallback);
		this.keyDownCallback && window.removeEventListener('keydown', this.keyDownCallback);
		this.resizeCallback && window.removeEventListener('resize', this.resizeCallback);
	}

	private measureStart() {
		this._startMeasureTimestamp = performance.now();
	}

	private measureStop() {
		if (this._startMeasureTimestamp === -1) return;
		this.totalTime += performance.now() - this._startMeasureTimestamp;
		this._startMeasureTimestamp = -1;
	}
}
